import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Plane, MapPin, Clock } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { IMAGES, REGION_IMAGE } from "../lib/constants";
import { Reveal, MaskLine } from "../components/Reveal";
import Marquee from "../components/Marquee";

function Hero() {
  const { t } = useApp();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative h-[100svh] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroVan} alt="Private van" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
      </motion.div>

      <div className="relative h-full max-w-[1500px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="text-[10px] md:text-xs tracking-[0.35em] uppercase text-[var(--gold)] mb-6"
        >
          {t.hero.tag}
        </motion.p>

        <h1 className="font-serif font-light uppercase leading-[0.92] tracking-tighter text-white text-[15vw] sm:text-[12vw] md:text-[8.5vw]">
          <MaskLine delay={0.35}>{t.hero.l1}</MaskLine>
          <MaskLine delay={0.5}><span className="text-[var(--gold)] italic">{t.hero.l2}</span></MaskLine>
          <MaskLine delay={0.65}>{t.hero.l3}</MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9 }}
          className="mt-8 flex flex-col md:flex-row md:items-end gap-8 md:gap-16"
        >
          <p className="max-w-md text-white/70 text-base md:text-lg font-light leading-relaxed">{t.hero.sub}</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/book" data-testid="hero-book-button" className="btn-fill border border-[var(--gold)] text-[var(--gold)] px-8 py-4 text-xs uppercase tracking-[0.2em] flex items-center gap-3 transition-colors duration-300">
              {t.hero.cta} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#destinations" className="border border-white/25 text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:border-white transition-colors duration-300">
              {t.hero.secondary}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Manifesto() {
  const { t } = useApp();
  return (
    <section id="experience" className="relative bg-[var(--purple)] py-28 md:py-40 purple-radial">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <Reveal>
          <p className="text-xs tracking-[0.3em] uppercase text-[var(--gold)] mb-6">{t.manifesto.tag}</p>
          <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight max-w-3xl leading-tight">{t.manifesto.title}</h2>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-x-16 gap-y-14 mt-20">
          {t.manifesto.chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="flex gap-6 border-t border-white/15 pt-8">
                <span className="font-serif text-[var(--gold)] text-3xl md:text-4xl leading-none">{c.n}</span>
                <div>
                  <h3 className="text-lg uppercase tracking-[0.15em] mb-3">{c.t}</h3>
                  <p className="text-white/60 font-light leading-relaxed">{c.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Fleet() {
  const { t } = useApp();
  return (
    <section id="fleet" className="relative bg-[#050505] py-28 md:py-40">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative overflow-hidden">
            <img src={IMAGES.van2} alt="Private van fleet" className="w-full h-[420px] md:h-[560px] object-cover transition-transform duration-700 hover:scale-105" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            <div className="absolute bottom-0 left-0 bg-black/70 backdrop-blur-sm px-6 py-4 border-t border-r border-[var(--gold)]/40">
              <p className="text-[var(--gold)] text-xs tracking-[0.25em] uppercase">Private Van · up to 14</p>
            </div>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-[var(--gold)] mb-6">{t.fleet.tag}</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight leading-none">{t.fleet.title}</h2>
            <p className="text-white/60 font-light leading-relaxed mt-6 max-w-lg">{t.fleet.d}</p>
          </Reveal>
          <div className="grid grid-cols-2 gap-x-8 gap-y-5 mt-10">
            {t.fleet.features.map((f, i) => (
              <Reveal key={f} delay={i * 0.05}>
                <div className="flex items-center gap-3 border-b border-white/10 pb-3">
                  <span className="w-1.5 h-1.5 bg-[var(--gold)] rotate-45" />
                  <span className="text-sm text-white/75">{f}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Destinations() {
  const { t } = useApp();
  const [dests, setDests] = useState([]);

  useEffect(() => {
    api.get("/destinations").then((r) => setDests(r.data)).catch(() => {});
  }, []);

  return (
    <section id="destinations" className="relative bg-black py-28 md:py-40">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[var(--gold)] mb-6">{t.destinations.tag}</p>
              <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight">{t.destinations.title}</h2>
            </div>
            <p className="text-white/50 max-w-sm font-light">{t.destinations.sub}</p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {dests.map((d, i) => (
            <Reveal key={d.id} delay={(i % 3) * 0.08}>
              <Link
                to={`/book?dest=${d.id}`}
                data-testid={`dest-card-${d.id}`}
                className="group relative block h-72 overflow-hidden border border-[var(--border)]"
              >
                <img
                  src={REGION_IMAGE[d.region] || IMAGES.cancunBeach}
                  alt={d.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute top-4 left-4 text-[10px] tracking-[0.2em] uppercase text-white/70 bg-black/40 px-3 py-1 backdrop-blur-sm">
                  {d.region}
                </div>
                <div className="absolute bottom-0 inset-x-0 p-6">
                  <h3 className="font-serif text-2xl leading-tight mb-2">{d.name}</h3>
                  <div className="flex items-center gap-4 text-white/60 text-xs mb-4">
                    {d.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {d.duration}</span>}
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.booking.privateVan}</span>
                  </div>
                  {d.pricing_type === "private" && d.includes?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {d.includes.map((it) => (
                        <span key={it} className="text-[9px] uppercase tracking-wider text-[var(--gold)]/90 border border-[var(--gold)]/30 px-2 py-0.5">
                          {t.inc[it] || it}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-end justify-between">
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-white/50">
                        {d.pricing_type === "private" ? t.destinations.privateTour : t.destinations.from}
                      </span>
                      <div className="text-[var(--gold)] text-2xl font-serif">
                        ${d.price} <span className="text-xs text-white/50">USD {d.pricing_type === "private" ? "" : `/ ${t.destinations.perPerson}`}</span>
                      </div>
                    </div>
                    <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white group-hover:text-[var(--gold)] transition-colors">
                      {t.destinations.book} <ArrowRight className="w-4 h-4 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  const { t } = useApp();
  return (
    <section className="relative py-28 md:py-40 overflow-hidden">
      <img src={IMAGES.chauffeur} alt="Chauffeur" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/70" />
      <div className="relative max-w-[1500px] mx-auto px-5 md:px-10 text-center">
        <Reveal>
          <h2 className="font-serif font-light text-5xl md:text-7xl tracking-tight mb-6">{t.cta.title}</h2>
          <p className="text-white/60 mb-10 text-lg font-light">{t.cta.sub}</p>
          <Link to="/book" data-testid="cta-book-button" className="btn-fill inline-flex items-center gap-3 border border-[var(--gold)] text-[var(--gold)] px-12 py-5 text-sm uppercase tracking-[0.25em] transition-colors duration-300">
            {t.cta.btn} <ArrowRight className="w-5 h-5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div>
      <Hero />
      <Marquee />
      <Manifesto />
      <Fleet />
      <Destinations />
      <CTA />
    </div>
  );
}
